import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../../styles/restablecer-contrasena.module.css";
import { useAuth } from "../../api/AuthContext.jsx";
import LoaderOverlay from "../../components/LoaderOverlay.jsx";

function RestablecerContrasenaScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, cambiarContrasena, confirmarResetContrasena, logout } = useAuth();
  const [formData, setFormData] = useState({
    nuevaContrasena: "",
    confirmarContrasena: "",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Detectar si viene del enlace del email
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");
  const esResetPorEmail = !!(uid && token);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg("");
  };

  const validatePassword = () => {
    if (formData.nuevaContrasena.length < 8) {
      setErrorMsg("La contraseña debe tener al menos 8 caracteres");
      return false;
    }
    if (formData.nuevaContrasena !== formData.confirmarContrasena) {
      setErrorMsg("Las contraseñas no coinciden");
      return false;
    }
    // Validación de complejidad
    const hasUpperCase = /[A-Z]/.test(formData.nuevaContrasena);
    const hasLowerCase = /[a-z]/.test(formData.nuevaContrasena);
    const hasNumbers = /\d/.test(formData.nuevaContrasena);
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setErrorMsg("La contraseña debe contener mayúsculas, minúsculas y números");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }
    
    setLoading(true);
    setErrorMsg("");
    
    try {
      if (esResetPorEmail) {
        // Flujo: reset desde enlace del email (sin sesión)
        await confirmarResetContrasena(uid, token, formData.nuevaContrasena, formData.confirmarContrasena);
        setSuccessMsg("¡Contraseña restablecida correctamente! Redirigiendo al login...");
        setTimeout(() => navigate("/"), 2000);
      } else {
        // Flujo: primer acceso (usuario autenticado)
        await cambiarContrasena(formData.nuevaContrasena, formData.confirmarContrasena);
        setSuccessMsg("¡Contraseña actualizada correctamente! Redirigiendo al login...");
        setTimeout(() => {
          logout();
          navigate("/");
        }, 2000);
      }
    } catch (error) {
      let msg = "Error al cambiar la contraseña";
      if (error && error.message) {
        msg = error.message;
      } else if (error && typeof error === "object") {
        if (error.error) msg = error.error;
        else if (error.detail) msg = error.detail;
        else msg = JSON.stringify(error);
      }
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (esResetPorEmail) {
      navigate("/");
    } else {
      logout();
      navigate("/");
    }
  };

  const EyeIcon = ({ show }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {show ? (
        <path
          d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
          fill="#898D8D"
        />
      ) : (
        <path
          d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
          fill="#898D8D"
        />
      )}
    </svg>
  );

  return (
    <main className={styles.loginScreen} role="main">
      <div className={styles.loginContainer}>
        <LoaderOverlay visible={loading} />
        <header>
          <div className={styles.logoContainer}>
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/b2a2315bcd112541ac469d7dc89567dac6bc3d40?width=164"
              alt="Universidad Lux Logo"
              className={styles.logoImage}
            />
          </div>
          <div className={styles.universityTitle}>
            <span className={styles.universityText}>UNIVERSIDAD</span>
            <div className={styles.titleDivider} />
            <span className={styles.luxText}>LUX</span>
          </div>
        </header>

        <div className={styles.welcomeSection}>
          <h1 className={styles.welcomeTitle}>¡Bienvenido!</h1>
          <p className={styles.welcomeSubtitle}>
            {esResetPorEmail
              ? 'Ingresa tu nueva contraseña para recuperar el acceso a tu cuenta.'
              : user && user.primer_acceso
              ? 'Es tu primer inicio de sesión. Por favor, establece una nueva contraseña para continuar.'
              : '¿Olvidaste tu contraseña? Ingresa una nueva contraseña para recuperar el acceso a tu cuenta.'}
          </p>
          {!esResetPorEmail && user && (
            <p className={styles.userEmail}>{user.email}</p>
          )}
        </div>

        <form className={styles.formContainer} onSubmit={handleSubmit}>
          {/* Nueva Contraseña */}
          <div>
            <label htmlFor="nuevaContrasena" className={styles.fieldLabel}>
              Nueva Contraseña
            </label>
            <div className={styles.passwordWrapper}>
              <div className={styles.passwordField}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="nuevaContrasena"
                  name="nuevaContrasena"
                  value={formData.nuevaContrasena}
                  onChange={handleInputChange}
                  placeholder="Ingresa tu nueva contraseña"
                  className={styles.inputText}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  <EyeIcon show={showPassword} />
                </button>
              </div>
            </div>
          </div>

          {/* Confirmar Contraseña */}
          <div>
            <label htmlFor="confirmarContrasena" className={styles.fieldLabel}>
              Confirmar Contraseña
            </label>
            <div className={styles.passwordWrapper}>
              <div className={styles.passwordField}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmarContrasena"
                  name="confirmarContrasena"
                  value={formData.confirmarContrasena}
                  onChange={handleInputChange}
                  placeholder="Confirma tu nueva contraseña"
                  className={styles.inputText}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  <EyeIcon show={showConfirmPassword} />
                </button>
              </div>
            </div>
          </div>

          {/* Requisitos de contraseña */}
          <div className={styles.passwordRequirements}>
            <p className={styles.requirementsTitle}>La contraseña debe tener:</p>
            <ul className={styles.requirementsList}>
              <li className={formData.nuevaContrasena.length >= 8 ? styles.requirementMet : ""}>
                Al menos 8 caracteres
              </li>
              <li className={/[A-Z]/.test(formData.nuevaContrasena) ? styles.requirementMet : ""}>
                Una letra mayúscula
              </li>
              <li className={/[a-z]/.test(formData.nuevaContrasena) ? styles.requirementMet : ""}>
                Una letra minúscula
              </li>
              <li className={/\d/.test(formData.nuevaContrasena) ? styles.requirementMet : ""}>
                Un número
              </li>
            </ul>
          </div>

          {/* Mensajes de error/éxito */}
          {errorMsg && (
            <div className={styles.errorMessage} role="alert">
              <span className={styles.errorIcon}>!</span>
              {errorMsg}
            </div>
          )}
          
          {successMsg && (
            <div className={styles.successMessage} role="alert">
              <span className={styles.successIcon}>✓</span>
              {successMsg}
            </div>
          )}

          {/* Botón de envío */}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading || successMsg}
          >
            {loading ? "Actualizando..." : "Establecer Contraseña"}
          </button>
        </form>

        {/* Enlace para cerrar sesión */}
        <button
          type="button"
          className={styles.logoutLink}
          onClick={handleLogout}
        >
          Cancelar y cerrar sesión
        </button>
      </div>
    </main>
  );
}

export default RestablecerContrasenaScreen;
